import { MeasurementPresentation } from "../../services/programInsights.service";
import {
	ArrangementSurface,
	DeclaredAs,
	PresentationOffer,
	ProgramFigure,
	ReasonField,
	UnavailableReason,
} from "../../services/arrangements.service";

/**
 * SIMS-8555 — every word the arrangement screen shows about a choice. The server
 * decides what is offered (the presentation matrix, ratified Q-P1) and why not;
 * this file only names it. AC4: each unavailable presentation is shown with its
 * reason and the declaration field that would have to change (decision C8).
 */

export const SURFACE_LABEL: Record<ArrangementSurface, string> = {
	PROGRAM_DASHBOARD: "Program Dashboard",
	PARTICIPANT_STATISTICS: "Participant statistics",
};

/** 8c's vocabulary: seven presentations, closed (FR-28). */
export const PRESENTATION_LABEL: Record<MeasurementPresentation, string> = {
	TREND: "Trend chart",
	LATEST_VALUE: "Latest value",
	AVERAGE: "Average",
	DAYS_MEETING_TARGET: "Days meeting target",
	TOTAL: "Total",
	COUNT: "Count",
	BREAKDOWN: "Breakdown by category",
};

/** The matrix's stated reasons (Confluence 1552121858, step 5), in the author's words. */
const REASON_WORDS: Record<UnavailableReason, string> = {
	NEEDS_TARGET_AND_PER_DAY:
		"Needs a declared target and a value on each protocol day",
	NO_VALUE_TREND_PER_OCCURRENCE:
		"A per-occurrence measurement has no daily value to plot",
	DOES_NOT_SUM:
		"This quantity doesn't add up: percentages, averages and scores don't sum",
	TALLY_TOTAL_IS_COUNT: "For a count, the total is the count: choose Count",
	NO_CATEGORIES: "Needs a discrete result to break down",
	COVERAGE_NOT_A_PRESENTATION:
		"Counting the days it was assessed is coverage, not a presentation of this measurement",
	REDUNDANT_ON_RUNNING_TOTAL:
		"A running total only grows; its total already says what this would",
	MEANINGLESS_PER_OCCURRENCE_COUNT:
		"Each occurrence counts one, so this has no meaning here",
	NO_LATEST_CATEGORY: "A discrete result has no single latest figure to show",
	CATEGORY_NOT_A_NUMBER: "Results are counted, not measured",
	CATEGORY_HAS_NO_TARGET: "A discrete result carries no target",
};

const FIELD_WORDS: Record<ReasonField, string> = {
	comparator: "target",
	frequency: "frequency",
	quantity: "quantity",
};

/** "Needs a declared target… — the declaration's target would have to change." */
export const unavailableWords = (offer: PresentationOffer): string => {
	const reason = offer.reason
		? REASON_WORDS[offer.reason]
		: "Not offered for this declaration";
	return offer.field
		? `${reason}. The declaration's ${FIELD_WORDS[offer.field]} would have to change.`
		: `${reason}.`;
};

/** What a chosen, offered presentation shows (8c's per-choice line). */
export const offeredWords = (
	presentation: MeasurementPresentation,
	isDefault: boolean,
): string => {
	const what: Record<MeasurementPresentation, string> = {
		TREND: "A full-row chart over the whole plan",
		LATEST_VALUE: "The most recent value, as a tile",
		AVERAGE: "The average, stating what it is taken over",
		DAYS_MEETING_TARGET:
			"Days that met that day's target, of the days scheduled",
		TOTAL: "The sum, as a tile",
		COUNT: "How many times it was recorded, as a tile",
		BREAKDOWN: "Each configured result with its count and share",
	};
	return isDefault
		? `Default for this declaration · ${what[presentation]}`
		: what[presentation];
};

/** AC6: a presentation implies its width. Shown so the author knows; never a control. */
export const widthWords = (presentation: MeasurementPresentation): string =>
	presentation === "TREND" ? "Full row" : "Tile";

const QUANTITY_WORDS: Record<string, string> = {
	PERCENTAGE_IN_RANGE: "percentage in range",
	AVERAGE_VALUE: "average value",
	SCORE: "score",
	COUNT_OF_OCCURRENCES: "counting",
	DURATION: "duration",
	DISCRETE_OUTCOME: "discrete result",
};

const FREQUENCY_WORDS: Record<string, string> = {
	PER_PROTOCOL_DAY: "once per protocol day",
	PER_OCCURRENCE: "once per occurrence",
	RUNNING_TOTAL: "running total",
};

const COMPARATOR_WORDS: Record<string, string> = {
	NONE: "no target",
	AT_OR_ABOVE: "target at or above",
	AT_OR_BELOW: "target at or below",
};

/** A vocabulary value in words; one the screen doesn't know is shown as it is, never a crash. */
const say = (
	words: Record<string, string>,
	value: string | undefined,
): string =>
	value ? (words[value] ?? value.toLowerCase().replace(/_/g, " ")) : "—";

/** 8b's "Declared as": "discrete result · once per occurrence · no target". */
export const declaredAsWords = (d: DeclaredAs | null): string =>
	d
		? [
				say(QUANTITY_WORDS, d.quantity),
				say(FREQUENCY_WORDS, d.frequency),
				say(COMPARATOR_WORDS, d.comparator),
			].join(" · ")
		: "—";

/** The overview's own titles (SIMS-8539), so a figure reads the same here as on the dashboard. */
export const FIGURE_LABEL: Record<ProgramFigure, string> = {
	INCENTIVES_EARNED: "Total incentives earned",
	INCENTIVES_RECEIVED: "Incentives received",
	INCENTIVES_BANKED: "Incentives banked",
	SHARE_OF_AVAILABLE: "Share of available incentives",
	ENROLLMENTS_BY_STATUS: "Enrollments by status",
	PROVIDERS_ENROLLED: "Providers enrolled",
	AVERAGE_LENGTH_OF_TREATMENT: "Average length of treatment",
};

/** A summary's name on the picker (AC12). */
export const SUMMARY_LABEL: Partial<Record<MeasurementPresentation, string>> = {
	COUNT: "Count",
	TOTAL: "Total",
	LATEST_VALUE: "Latest value",
	AVERAGE: "Average",
};
